import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  InputGroup,
  InputGroupText,
  InputGroupAddon,
} from "reactstrap";
import OTPScreen from "./OTPScreen";
import { REGEX, OTP_TIMER } from "constants/CommonConstants";
import { addVisitor, verifyOTP, resendOTP } from "action/auth";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";

const VisitorVerifyModal = ({ isOpen, onClose, setUniqueID }) => {
  const inputsRef = useRef([]);
  const { notify } = useContext(NotificationContext);

  const [visitorForm, setVisitorForm] = useState({
    name: { value: "", error: "" },
    email: { value: "", error: "" },
    mobile: { value: "", error: "" },
    designation: { value: "", error: "" },
    organization: { value: "", error: "" },
  });

  const [checked, setChecked] = useState({ value: false, error: "" });
  const [otpState, setOtpState] = useState({
    isOtpMode: false,
    otp: ["", "", "", "", "", ""],
    timer: OTP_TIMER,
    error: "",
    otherData: null,
  });

  const [showLoader, setShowLoader] = useState({
    getOtp: false,
    verifyOtp: false,
  });

  const [policySheet, setPolicySheet] = useState({
    open: false,
    type: null, // "terms" | "privacy"
  });

  useEffect(() => {
    if (!otpState.isOtpMode) return;
    if (otpState.timer === 0) return;

    const interval = setInterval(
      () => setOtpState((prev) => ({ ...prev, timer: prev.timer - 1 })),
      1000,
    );
    return () => clearInterval(interval);
  }, [otpState.timer, otpState.isOtpMode]);

  //   useEffect(() => {
  //   const checkVisitor = async () => {
  //     try {
  //       await api.get("/visitor/status"); // cookie auto sent
  //       setIsVerified(true);
  //     } catch (err) {
  //       setIsVerified(false);
  //       setShowVerifyModal(true); // key point
  //     }
  //   };

  //   checkVisitor();
  // }, []);

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "name":
        if (!value.trim()) {
          error = "Name is required";
        } else if (!REGEX.ONLY_ALPHABETS_REGEX.test(value)) {
          error = "Please enter a valid name";
        }
        break;

      case "organization":
        if (!value.trim()) {
          error = "Organization is required";
        }
        break;

      case "mobile":
        if (!value) {
          error = "Mobile number is required";
        } else if (!REGEX.PHONE_NO_REGEX.test(value)) {
          error = "Please enter a valid 10-digit mobile number";
        }
        break;

      case "email":
        if (value && !REGEX.EMAIL_REGEX.test(value)) {
          error = "Please enter a valid email address";
        }
        break;

      default:
        break;
    }

    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);

    setVisitorForm((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        error,
      },
    }));
  };

  const handleVisitorChange = (e) => {
    const { name, value } = e.target;

    setVisitorForm((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value,
        error: "",
      },
    }));
  };

  const validateBeforeSendOtp = () => {
    let isValid = true;
    const updatedForm = { ...visitorForm };

    ["name", "organization", "mobile"].forEach((field) => {
      const error = validateField(field, visitorForm[field].value);

      updatedForm[field] = {
        ...updatedForm[field],
        error,
      };

      if (error) isValid = false;
    });

    setVisitorForm(updatedForm);
    return isValid;
  };

  const handleSendOtp = async () => {
    if (!validateBeforeSendOtp()) return;

    if (!checked.value) {
      setChecked((prev) => ({
        ...prev,
        error:
          "Please confirm that you agree to the Terms & Conditions and Privacy Policy.",
      }));
      return;
    }

    setShowLoader((prev) => ({ ...prev, getOtp: true }));
    try {
      const bodyData = {
        mobile: visitorForm.mobile.value,
        name: visitorForm.name.value,
        organization: visitorForm.organization.value,
        ...(visitorForm.designation.value && {
          designation: visitorForm.designation.value,
        }),
        ...(visitorForm.email.value && {
          email: visitorForm.email.value,
        }),
      };
      const res = await addVisitor(bodyData);
      if (res.code === 200) {
        setOtpState((prev) => ({
          ...prev,
          isOtpMode: true,
          timer: OTP_TIMER,
          otherData: res.data,
        }));
        inputsRef.current[0]?.focus();
        notify("success", res.message || "OTP has been resent successfully.");

        // this is temp code to auto fill mobile otp received from backend
        // if (res?.data?.mobileOTP) {
        //   if (!REGEX.OTP_STRING_REGEX.test(res?.data?.mobileOTP)) return;

        //   const digits = res?.data?.mobileOTP.slice(0, 6).split("");

        //   setTimeout(() => {
        //     setOtpState((prev) => ({
        //       ...prev,
        //       otp: digits,
        //       error: "",
        //       otherData: res.data,
        //     }));
        //     inputsRef.current[5]?.focus();
        //   }, 1000);

        //   if (digits.length === 6) {
        //     setTimeout(() => {
        //       handleVerifyOtp(digits, res.data);
        //     }, 2000);
        //   }
        // }
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to resend OTP, try again later.";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Something went wrong, try again later.");
    } finally {
      setShowLoader((prev) => ({ ...prev, getOtp: false }));
    }
  };

  // This function is used to navigate back to step 1 of registration
  const handleBack = () => {
    setOtpState((prev) => ({
      ...prev,
      isOtpMode: false,
      otp: ["", "", "", "", "", ""],
      timer: OTP_TIMER,
      error: "",
    }));
    setVisitorForm((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([key, field]) => [key, { ...field }]),
      ),
    );
  };

  const handleOtpChange = (value, index) => {
    if (!REGEX.ONLY_DIGITS_REGEX.test(value)) return;

    let newOtp = [...otpState.otp];
    newOtp[index] = value;
    setOtpState((prev) => ({ ...prev, otp: newOtp, error: "" }));

    if (value && index < 5) inputsRef.current[index + 1].focus();

    const finalOtp = newOtp.join("");
    // if (finalOtp.length === 6 && !newOtp.includes("")) {
    //   setTimeout(() => {
    //     handleVerifyOtp(newOtp);
    //   }, 500); // auto verify
    // }
  };

  // Handle enter key event to verify otp and also backspace to move focus to previous box
  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Enter") {
      setTimeout(() => {
        handleVerifyOtp(otpState.otp);
      }, 500); // auto verify
      return;
    }
    if (e.key === "Backspace" && !otpState.otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  // Handle paste event to fill OTP boxes
  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text").slice(0, 6);
    if (!REGEX.OTP_STRING_REGEX.test(text)) return;

    const digits = text.split("");

    setOtpState((prev) => ({
      ...prev,
      otp: prev.otp.map((_, i) => digits[i] || ""),
      error: "",
    }));
    // if (digits.length === 6) {
    //   inputsRef.current[5].focus();
    //   setTimeout(() => {
    //     handleVerifyOtp(digits);
    //   }, 500); // auto verify
    // }
  };

  // resend otp functionality
  const handleResendOtp = async () => {
    try {
      const bodyData = {
        uuid: otpState.otherData.uuid,
      };
      const res = await resendOTP(bodyData);
      if (res.code === 200) {
        setOtpState((prev) => ({
          ...prev,
          otp: ["", "", "", "", "", ""],
          timer: OTP_TIMER,
          error: "",
          otherData: { ...prev.otherData, ...res.data },
        }));
        inputsRef.current[0].focus();
        notify("success", res.message || "OTP has been resent successfully.");

        // this is temp code to auto fill mobile otp received from backend
        // if (res?.data?.mobileOTP) {
        //   if (!REGEX.OTP_STRING_REGEX.test(res?.data?.mobileOTP)) return;

        //   const digits = res?.data?.mobileOTP.slice(0, 6).split("");

        //   setOtpState((prev) => ({
        //     ...prev,
        //     otp: digits,
        //     error: "",
        //     otherData: { ...prev.otherData, ...res.data },
        //   }));

        //   inputsRef.current[5]?.focus();

        //   if (digits.length === 6) {
        //     setTimeout(() => {
        //       handleVerifyOtp(digits, res.data);
        //     }, 2000);
        //   }
        // }
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to resend OTP, try again later.";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Something went wrong, try again later.");
    } finally {
    }
  };

  // Verify OTP handler
  const handleVerifyOtp = async (otp, otherData = {}) => {
    const otpArray = Array.isArray(otp) ? otp : otpState.otp;
    const userOTP = otpArray?.join("");

    if (userOTP.length === 6) {
      setShowLoader((prev) => ({ ...prev, verifyOtp: true }));
      try {
        const bodyData = {
          otp: userOTP,
          uuid: otherData.uuid || otpState.otherData.uuid,
          otpPurpose: otherData.otpPurpose || otpState.otherData.otpPurpose,
        };

        const res = await verifyOTP(bodyData);
        if (res?.code === 200 || res?.data?.visitorUniqueID) {
          notify("success", res.message || "OTP verification successful.");

          setOtpState({
            isOtpMode: false,
            otp: ["", "", "", "", "", ""],
            timer: OTP_TIMER,
            error: "",
            otherData: null,
          });
          setUniqueID(res?.data?.visitorUniqueID);
          onClose(res?.data?.visitorUniqueID);
        } else {
          const message = Array.isArray(res?.message)
            ? res?.message[0]?.Msg
            : res?.message || "OTP verification failed, try again later.";
          notify("danger", message);
        }
      } catch (error) {
        notify("danger", "Something went wrong, try again later.");
      } finally {
        setShowLoader((prev) => ({ ...prev, verifyOtp: false }));
      }
    } else {
      const otpMessage = `Please enter the 6-digit OTP sent to your mobile number`;
      setOtpState((prev) => ({ ...prev, error: otpMessage }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      centered
      backdrop="static"
      keyboard={false}
      className="sv-entry-modal"
      size="md"
      backdropClassName="sv-blur-backdrop"
    >
      <ModalHeader className="border-0 pb-0">
        <div>
          <h2 className="mb-0 fw-bold">Raychem RPG Jointer Verification Portal</h2>
          <p className="text-muted mb-1">
            Kindly fill in the form below to access the jointer’s details.
          </p>
        </div>
      </ModalHeader>
      <div className="sv-modal-divider mt-2 mx-3" />
      <ModalBody>
        {otpState.isOtpMode ? (
          <OTPScreen
            otpState={otpState}
            inputsRef={inputsRef}
            handlePaste={handlePaste}
            handleOtpChange={handleOtpChange}
            handleOtpKeyDown={handleOtpKeyDown}
            resendOtp={handleResendOtp}
            verifyOtp={handleVerifyOtp}
            enterdValue={visitorForm.mobile.value}
            showLoader={showLoader}
            showChangeIdentifier={true}
            onChangeIdentifier={handleBack}
          />
        ) : (
          <>
            <div className="sv-modal-field mt--2">
              <Label>
                Name <span className="text-danger">*</span>
              </Label>
              <Input
                name="name"
                value={visitorForm.name.value}
                onChange={handleVisitorChange}
                placeholder="Enter your name"
                onBlur={handleBlur}
                maxLength={100}
                autoComplete="off"
              />
              {visitorForm.name.error && (
                <div className="invalid-feedback d-block">
                  {visitorForm.name.error}
                </div>
              )}
            </div>

            <div className="sv-modal-field">
              <Label>
                Organization <span className="text-danger">*</span>
              </Label>
              <Input
                name="organization"
                value={visitorForm.organization.value}
                onChange={handleVisitorChange}
                placeholder="Enter organization"
                onBlur={handleBlur}
                maxLength={100}
                autoComplete="off"
              />
              {visitorForm.organization.error && (
                <div className="invalid-feedback d-block">
                  {visitorForm.organization.error}
                </div>
              )}
            </div>

            <div className="sv-modal-field">
              <Label>Designation (Optional)</Label>
              <Input
                name="designation"
                value={visitorForm.designation.value}
                onChange={handleVisitorChange}
                placeholder="Enter designation"
                onBlur={handleBlur}
                maxLength={100}
                autoComplete="off"
              />
              {visitorForm.designation.error && (
                <div className="invalid-feedback d-block">
                  {visitorForm.designation.error}
                </div>
              )}
            </div>

            <div className="sv-modal-field">
              <Label>
                Mobile Number <span className="text-danger">*</span>
              </Label>
              <InputGroup className="input-group">
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <img
                      src="https://flagcdn.com/w20/in.png"
                      alt="India"
                      width="20"
                      height="14"
                      className="flags"
                    />
                    +91
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  name="mobile"
                  value={visitorForm.mobile.value}
                  onChange={handleVisitorChange}
                  placeholder="Enter mobile number"
                  maxLength={10}
                  onBlur={handleBlur}
                  autoComplete="off"
                />
              </InputGroup>
              {visitorForm.mobile.error && (
                <div className="invalid-feedback d-block">
                  {visitorForm.mobile.error}
                </div>
              )}
            </div>

            <div className="sv-modal-field">
              <Label>Email (Optional)</Label>
              <Input
                type="email"
                name="email"
                value={visitorForm.email.value}
                onChange={handleVisitorChange}
                placeholder="Enter email address"
                onBlur={handleBlur}
                autoComplete="off"
              />
              {visitorForm.email.error && (
                <div className="invalid-feedback d-block">
                  {visitorForm.email.error}
                </div>
              )}
            </div>
            <div>
              <div
                className="d-flex align-items-center cursor-pointer mt-4"
                onClick={(e) =>
                  setChecked((prev) => ({ value: !prev.value, error: "" }))
                }
              >
                <i
                  className={
                    checked.value
                      ? "fa-solid fa-square-check text-deep-blue mr-2 fa-lg"
                      : "fa-regular fa-square mr-2 fa-lg"
                  }
                ></i>
                <span className="text-muted text-sm">
                  I agree to the{" "}
                  <span
                    className="span-link"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPolicySheet({ open: true, type: "terms" });
                    }}
                  >
                    Terms & Conditions
                  </span>{" "}
                  and{" "}
                  <span
                    className="span-link"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPolicySheet({ open: true, type: "privacy" });
                    }}
                  >
                    Privacy Policy
                  </span>
                </span>
              </div>
              {checked.error && (
                <div className="invalid-feedback d-block">{checked.error}</div>
              )}
            </div>
          </>
        )}
      </ModalBody>

      {!otpState.isOtpMode && (
        <ModalFooter className="border-0 pt-0">
          <Button
            className="btn-vivid-red w-100"
            onClick={handleSendOtp}
            disabled={!!showLoader.getOtp}
          >
            {showLoader.getOtp ? (
              <i className="fa fa-spinner fa-spin fa-xl text-white" />
            ) : (
              "Send OTP"
            )}
          </Button>
        </ModalFooter>
      )}

      {policySheet.open && (
        <div className={`policy-sheet ${policySheet.closing ? "closing" : ""}`}>
          <div className="policy-sheet-header">
            <h2 className="mb-0 ml-3 mt-2">
              {policySheet.type === "terms"
                ? "Terms & Conditions"
                : "Privacy Policy"}
            </h2>
            <i
              className="fa-solid fa-xmark pointer"
              onClick={() => {
                setPolicySheet((prev) => ({ ...prev, closing: true }));

                setTimeout(() => {
                  setPolicySheet({ open: false, type: null, closing: false });
                }, 300); // same as slideDown duration
              }}
            />
          </div>

          <div className="policy-sheet-body">
            {policySheet.type === "terms" ? (
             <div className="svp-card-content scrollable">
            <h3>1. Eligibility and Authorised Use</h3>
            <p>
              The TMS is for authorised employees and approved stakeholders of
              Raychem RPG. Unauthorised access, misuse, or circumvention of
              controls is prohibited.
            </p>

            <h3>2. User Responsibilities</h3>
            <p>
              You must keep credentials confidential; provide accurate
              information; refrain from uploading unlawful, harmful, or
              infringing content; and comply with all applicable policies and
              laws.
            </p>

            <h3>3. Intellectual Property</h3>
            <p>
              All training materials, software, and content in the TMS are owned
              by or licensed to Raychem RPG and are protected by applicable law.
              You may not copy, distribute, modify, or create derivative works
              without prior written permission.
            </p>

            <h3>4. Privacy and Data Protection</h3>
            <p>
              Use of the TMS is subject to the Privacy Policy (Part A). You must
              not upload personal data of others without a lawful basis or
              consent commensurate with the DPDP framework.
            </p>

            <h3>5. Availability and Maintenance</h3>
            <p>
              While we strive for high availability, service may be interrupted
              for maintenance or due to events beyond our reasonable control.
            </p>

            <h3>6. Security</h3>
            <p>
              You agree not to probe, scan, or test vulnerabilities or violate
              security or authentication measures. Security incidents must be
              reported promptly via emailing the Grievance Officer.
            </p>

            <h3>7. Termination</h3>
            <p>
              We may suspend or terminate access for policy violations, security
              risks, or upon end of engagement. Upon termination, we may retain
              or delete data in accordance with the Privacy Policy and legal
              requirements.
            </p>

            <h3>8. Disclaimers and Limitation of Liability</h3>
            <p>
              To the extent permitted by law, Raychem RPG is not liable for
              indirect, incidental, or consequential damages arising from TMS
              use. Nothing excludes liability required by applicable law.
            </p>

            <h3>9. Governing Law and Dispute Resolution</h3>
            <p>
              These Terms are governed by the laws of India. Courts with
              appropriate jurisdiction in Mumbai shall have jurisdiction,
              subject to applicable law.
            </p>
          </div>
            ) : (
              <div className="svp-card-content scrollable">
                <h3>1. Introduction</h3>
                <p>
                  Raychem RPG Pvt. Ltd. ("Raychem RPG", "we", "our", "us")
                  operates the Training Management System ("TMS"). For the
                  purposes of the Digital Personal Data Protection Act, 2023
                  ("DPDP Act"), Raychem RPG is the Data Fiduciary that
                  determines the purposes and means of processing personal data
                  within the TMS. By using the TMS, you acknowledge that your
                  personal data will be processed for clear, lawful, and limited
                  purposes connected to training administration. This Privacy
                  Policy explains what we collect, why, how long we keep it, who
                  we share it with, and the rights available to you.
                </p>

                <h3>2. Scope</h3>
                <p>
                  This Policy applies to digital personal data processed in
                  connection with the TMS, including personal data collected
                  online or collected offline and subsequently digitised. It
                  also applies where the TMS is accessed outside India for the
                  purpose of services offered in India.
                </p>

                <h3>3. Categories of Personal Data We Process</h3>
                <ul>
                  <li>
                    Identity and Employment: name, employee ID, designation,
                    department, manager mapping.
                  </li>
                  <li>
                    Contact: official email address and business phone number.
                  </li>
                  <li>
                    Training Data: enrolments, attendance, assessments,
                    certifications, feedback, learning paths, badges.
                  </li>
                  <li>
                    System & Security: login timestamps, activity logs,
                    device/browser details, IP address (for fraud prevention,
                    audit, and troubleshooting).
                  </li>
                </ul>
                <p>
                  We do not intentionally collect Sensitive Personal Data as
                  defined under the Information Technology Act Sensitive
                  Personal Data and Information Rules, 2011 (“IT Act SPDI
                  Rules”) (e.g., passwords, financial data, health or biometric
                  data) through the TMS unless explicitly notified and consented
                  for a specific lawful purpose.
                </p>

                <h3>4. Lawful Basis & Purposes of Processing</h3>
                <p>
                  We process personal data on the basis of your consent and/or
                  for legitimate uses permitted by the DPDP Act. Primary
                  purposes include:
                </p>
                <ol class="roman-parenthesis">
                  <li>managing training programs and curricula;</li>
                  <li>tracking progress, assessments and certifications;</li>
                  <li>
                    generating analytics for training effectiveness and
                    compliance;
                  </li>
                  <li>
                    information security, auditing and incident response; and
                  </li>
                  <li>complying with legal or regulatory requirements.</li>
                </ol>
                <p>
                  Personal data will not be used for any incompatible purpose
                  without fresh consent where required.
                </p>

                <h3>5. Notice and Consent</h3>
                <p>
                  Before or at the time of collection based on consent, we
                  provide a clear notice describing the data collected,
                  purposes, the rights available to you, and how to contact the
                  Grievance Officer. Consent is free, specific, informed, and
                  unambiguous and may be withdrawn at any time via the process
                  described in Section 9 of this Policy.
                </p>

                <h3>6. Data Sharing and Processing on Our Behalf</h3>
                <p>
                  We may share personal data with authorised Data Processors
                  solely to operate, secure, or support the TMS under written
                  contracts that require confidentiality, security, breach
                  reporting and deletion/return of data upon termination.
                  Personal data may be disclosed to lawful authorities where
                  required by applicable law.
                </p>

                <h3>7. Cross‑Border Transfers</h3>
                <p>
                  Where cross‑border transfer is necessary, it will be
                  undertaken in accordance with the DPDP framework (including
                  any notified country restrictions) and with appropriate
                  contractual safeguards. If data is stored solely in India, we
                  will state that in the TMS notice.
                </p>

                <h3>8. Security Measures</h3>
                <p>
                  We implement reasonable technical and organisational
                  safeguards aligned with recognised standards (such as ISO/IEC
                  27001) to protect personal data against unauthorised access,
                  alteration, disclosure or loss. Access is role‑based and
                  logged. Employees and contractors are subject to
                  confidentiality obligations.
                </p>

                <h3>9. Data Retention and Deletion</h3>
                <p>
                  We retain personal data only for as long as necessary to
                  fulfil the purposes described above or to comply with legal,
                  regulatory, audit, or enforceable contractual obligations.
                  Where retention is no longer required, data is securely
                  deleted or irreversibly anonymised. Upon consent withdrawal,
                  processing ceases except where retention is required under
                  law.
                </p>

                <h3>10. Your Rights</h3>
                <p>Subject to applicable law, you have the right to:</p>
                <ol class="roman-parenthesis">
                  <li>access information about your personal data;</li>
                  <li>request correction and erasure;</li>
                  <li>seek grievance redressal; and</li>
                  <li>
                    information security, auditing and incident response; and
                  </li>
                  <li>
                    nominate an individual to exercise your rights in the event
                    of incapacity or death.
                  </li>
                </ol>
                <p>
                  You may exercise these rights using the channels in Section 12
                  of this Policy.
                </p>

                <h3>11. Personal Data Breach</h3>
                <p>
                  In the event of a personal data breach, we will act without
                  undue delay in accordance with the DPDP framework, including
                  required intimations to the Data Protection Board of India and
                  affected individuals, and will take reasonable remedial steps.
                </p>

                <h3>12. Contact & Grievance Redressal</h3>
                <p>
                  Grievance Officer: [Name], Raychem RPG Pvt. Ltd., [Address].
                  Email: [email@raychemrpg.com]. Phone: [+91-XXXXXXXXXX].
                </p>
                <p>
                  You may raise a request/complaint through the TMS helpdesk or
                  by emailing the Grievance Officer. We will acknowledge and
                  address your request within statutory timelines.
                </p>

                <h3>13. Changes to this Policy</h3>
                <p>
                  We may update this Policy from time to time. Material changes
                  will be communicated within the TMS, and where required, we
                  will seek fresh consent.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default VisitorVerifyModal;
